import { React, useState, useEffect, useMemo, useContext } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress } from "@mui/material";
import CustomizedTable from "../../CustomizedTable/CustomizedTable";
import { DefaultCell } from "../../CustomizedTable/CustomCells/CustomCells";
import OfferStatusCell from "../../CustomizedTable/CustomCells/OfferStatusCell/OfferStatusCell";
import DateTimeRangeCell from "../../CustomizedTable/CustomCells/DateTimeRangeCell/DateTimeRangeCell";
import CurrencyCell from "../../CustomizedTable/CustomCells/CurrencyCell/CurrencyCell";
import CarrierNameCell from "../../CustomizedTable/CustomCells/CarrierNameCell/CarrierNameCell";
import MilesCell from "../../CustomizedTable/CustomCells/MilesCell/MilesCell";
import DateRangeFilter from "../../CustomizedTable/Filters/DateRangeFilter/DateRangeFilter";
import OfferStatusFilter from "../../CustomizedTable/Filters/OfferStatusFilter/OfferStatusFilter";
import NumberRangeFilter from "../../CustomizedTable/Filters/NumberRangeFilter/NumberRangeFilter";
import MultiOptionsFilter from "../../CustomizedTable/Filters/MultipleOptionsFilter/MultipleOptionsFilter";
import PrimarySelect from "../../Inputs/PrimarySelect/PrimarySelect";
import SecondaryButton from "../../Buttons/SecondaryButton/SecondaryButton";
import { sortArrayOfDateObjectsDesc } from "../../../utils/sortHelpers";
import { CarrierService } from "../../../services/CarrierService";
import { AlertContext } from "../../Dashboard/Dashboard";

export default function LoadProfileOffers({
  activeLoad,
  handleTableAction
}) {
  let navigate = useNavigate();
  const { instance } = useMsal();
  const [isLoading, setIsLoading] = useState(false);
  const [loadOffers, setLoadOffers] = useState([]);
  const [selectedOption, setSelectedOption] = useState(30);

  const offers = useMemo(() => loadOffers, [loadOffers]);

  const { warning } = useContext(AlertContext);

  const options = [
    {
      name: "Performance: 30 Days",
      value: 30
    },
    {
      name: "Performance: 60 Days",
      value: 60
    },
    {
      name: "Performance: 90 Days",
      value: 90
    }
  ];

  const columns = useMemo(
    () => [
      {
        Header: "Last Updated",
        accessor: "lastUpdatedDate",
        Filter: DateRangeFilter,
        filter: "dateBetween",
        width: 130,
        Cell: DateTimeRangeCell,
      },
      {
        Header: "Status",
        accessor: "approvedStatus",
        Filter: OfferStatusFilter,
        filter: "multi",
        width: 100,
        Cell: OfferStatusCell,
      },
      {
        Header: "Offer",
        accessor: "amount",
        Filter: NumberRangeFilter,
        filter: "between",
        width: 90,
        Cell: CurrencyCell,
      },
      {
        Header: "Counter",
        accessor: "counterOffer",
        Filter: NumberRangeFilter,
        filter: "between",
        width: 90,
        Cell: CurrencyCell,
      },
      {
        Header: "Carrier",
        accessor: "carrierName",
        idKey: "carrierId",
        filter: "fuzzyText",
        width: 240,
        showMetrics: true,
        Cell: CarrierNameCell,
      },
      {
        Header: "MC",
        accessor: "icc_number",
        filter: "fuzzyText",
        width: 80,
        Cell: DefaultCell,
      },
      {
        Header: "Miles to Origin",
        accessor: "miles",
        Filter: NumberRangeFilter,
        filter: "between",
        width: 140,
        Cell: MilesCell,
      },
      {
        Header: "City",
        accessor: "city",
        filter: "fuzzyText",
        width: 130,
        Cell: DefaultCell,
      },
      {
        Header: "State",
        accessor: "state",
        Filter: MultiOptionsFilter,
        filter: "multi",
        width: 80,
        Cell: DefaultCell,
      },
      {
        Header: "Empty",
        accessor: "expEmptyTime",
        Filter: DateRangeFilter,
        filter: "dateBetween",
        width: 130,
        Cell: DateTimeRangeCell,
      },
      {
        Header: "Note",
        accessor: "comments",
        filter: "fuzzyText",
        width: 600,
        Cell: DefaultCell,
      },
      {
        Header: "Carrier Rep",
        accessor: "enteredUserId",
        filter: "fuzzyText",
        width: 120,
        Cell: DefaultCell,
      },
    ],
    []
  );

  const getLoadOffers = () => {
    if (activeLoad.id) {
      setIsLoading(true);
      CarrierService.GetLoadOffers(instance, activeLoad.id, selectedOption).then(res => {
        let orderedOffers = sortArrayOfDateObjectsDesc(res.offers.filter(({ amount }) => amount != null), "lastUpdatedDate");

        orderedOffers = [
          ...orderedOffers.filter(({ approvedStatus }) => approvedStatus === "null" || approvedStatus === "4"),
          ...orderedOffers.filter(({ approvedStatus }) => approvedStatus != "null" && approvedStatus != "4" && approvedStatus != "7"),
          ...orderedOffers.filter(({ approvedStatus }) => approvedStatus === "7")
        ];

        orderedOffers.forEach(offer => {
          let carrierScorecard = res.carrierScorecard.find(({ carrierId }) => carrierId === offer.carrierId);
          if (carrierScorecard) {
            offer.orders = carrierScorecard.orderCount;
            offer.otp = Math.round(carrierScorecard.onTimePickup);
            offer.otd = Math.round(carrierScorecard.onTimeDelivery);
            offer.bounceCount = carrierScorecard.bounceCount;
            offer.bounce = Math.round(carrierScorecard.bounce);
            offer.sameDayBounce = Math.round(carrierScorecard.sameDayBounce);
            offer.techTracking = Math.round(carrierScorecard.techTracking);
          }
        });

        setLoadOffers(orderedOffers);
        setIsLoading(false);
      }).catch(error => {
        console.error(error);
        setIsLoading(false);
        warning(error);
      });
    }
  };

  useEffect(() => {
    getLoadOffers();
  }, [selectedOption]);

  useEffect(() => {
    if (activeLoad) {
      getLoadOffers();
    }
  }, [activeLoad]);

  return (
    <Box className="Load-profile-component">
      <Box className="Load-profile-component-header">
        <Typography variant="h3" color="black_opacity.100">
          Offers
        </Typography>
        <Box sx={{ display: "flex" }}>
          <PrimarySelect
            options={options.map((o) => (o))}
            onChange={setSelectedOption}
            value={selectedOption}
            nameKey="name"
            valueKey="value"
          />
          {/*2025-03-06 Per Jamal, hide this button*/}
          {/*{!location.pathname.includes("freight-matching") &&*/}
          {/*  <Box marginLeft="10px">*/}
          {/*    <SecondaryButton*/}
          {/*      label="Go to Offers Dashboard"*/}
          {/*      onClick={() => {*/}
          {/*        navigate(`/dashboard/offer-boards?orderId=${activeLoad?.id}`);*/}
          {/*      }}*/}
          {/*    />*/}
          {/*  </Box>*/}
          {/*}*/}
        </Box>
      </Box>
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
          <CircularProgress sx={{ color: "primary.60" }} size={40} />
        </Box>
      ) : (
        <CustomizedTable
          id="load-profile-offers"
          columns={columns}
          data={offers}
          actionCallback={handleTableAction}
        />
      )}
    </Box>
  );
}

LoadProfileOffers.propTypes = {
  activeLoad: PropTypes.object.isRequired,
  handleTableAction: PropTypes.func
};